import {
  discardWorkHomeProjectionPrefetch,
  prefetchWorkHomeProjection,
} from './briefing/work-home-projection';
import {
  discardSafetyBoardPagePrefetch,
  HOME_BOARD_ROWS,
  prefetchSafetyBoardPage,
} from './safety-board/use-safety-board-page';

/**
 * The work home's two consumers, started at the first moment they are known to be authorized.
 * They retain separate legacy ports, while v2 cutover coalesces them onto one composite request.
 *
 * The projection uses the authenticated session's explicit selected site. The board additionally
 * requires that site's public ID, so an unresolved scope never starts a board read.
 * Changing account or selected site discards the old one-shot handles before starting new ones.
 *
 * The warm-start handles are handed to the first screen that asks and then forgotten. The v2 seam
 * keeps its composite only for the shared ten-second startup window and clears it on session cache
 * lifecycle events; legacy mode keeps the two original calls unchanged.
 */
let warmStartedIdentityId: string | null = null;
let warmStartedSiteId: string | null = null;
let warmStartedScopeId: string | null = null;

export function beginHomeWarmStart(
  identityId: string,
  options: Readonly<{
    includeBoard?: boolean;
    siteId?: string | null;
    selectedScopeId?: string | null;
  }> = {},
): void {
  const siteId = options.includeBoard === false ? null : options.siteId ?? null;
  const selectedScopeId = options.selectedScopeId ?? null;
  if (
    !identityId ||
    (warmStartedIdentityId === identityId &&
      warmStartedSiteId === siteId &&
      warmStartedScopeId === selectedScopeId)
  ) return;
  endHomeWarmStart();
  warmStartedIdentityId = identityId;
  warmStartedSiteId = siteId;
  warmStartedScopeId = selectedScopeId;
  prefetchWorkHomeProjection(selectedScopeId);
  if (siteId) prefetchSafetyBoardPage(HOME_BOARD_ROWS, siteId);
}

/**
 * Drops a warm start whose home never opened -- a signed-out account, or a setup read that
 * failed and left the workspace unrendered. Whatever was in flight is let go rather than kept
 * for a home that may open much later, and the next confirmation starts again from scratch.
 */
export function endHomeWarmStart(): void {
  warmStartedIdentityId = null;
  warmStartedSiteId = null;
  warmStartedScopeId = null;
  discardWorkHomeProjectionPrefetch();
  discardSafetyBoardPagePrefetch();
}
